/**
 * Live updates for LISTS — Step 60.
 *
 * Kai: *"if someone adds a ticket it shows on the dashboard & ticket page
 * etc … most importantly it shows that it's updated by someone or something
 * else."*
 *
 * ── Why a list does not refresh itself ─────────────────────────────────
 *
 * A ticket page can refetch under you: the content changes in place and a
 * banner says who. A QUEUE cannot. The plan is explicit about it — *"never
 * optimistically reorder or remove a row … rows vanishing under the cursor is
 * the most disorienting thing a queue can do"* — and a refetch does exactly
 * that: the row you were reaching for moves because somebody in another office
 * changed a priority.
 *
 * So a list is TOLD and decides for itself. `pending` counts what has changed
 * since you last looked, the page offers it, and the refetch happens when the
 * person is ready for their rows to move. `auto` exists for surfaces where
 * nothing can move under a cursor — dashboard counts and charts — which can
 * take the update immediately.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { qk } from '../../lib/queryClient';
import { isLive, onLiveChange, subscribe } from '../../lib/realtime';

export function useOrgRealtime(opts: {
  /** Refetch as soon as a signal arrives. Only for surfaces with nothing to
   *  reach for — counts, charts. */
  auto?: boolean;
  /** What to invalidate when the person asks for it (or immediately, if
   *  `auto`). */
  invalidate: QueryKey[];
}): { pending: number; lastActor: string | null; live: boolean; refresh: () => void } {
  const qc = useQueryClient();
  const [pending, setPending] = useState(0);
  const [lastActor, setLastActor] = useState<string | null>(null);
  const [live, setLive] = useState(isLive());
  // A ref so the subscription is not torn down and rebuilt every time a key
  // array is re-created during a render.
  const keys = useRef(opts.invalidate);
  keys.current = opts.invalidate;
  const auto = opts.auto ?? false;

  useEffect(() => onLiveChange(setLive), []);

  useEffect(() => {
    const apply = () => {
      for (const key of keys.current) void qc.invalidateQueries({ queryKey: key });
    };
    return subscribe('org', {
      onChange: (signal) => {
        setLastActor(signal.actorName ?? null);
        if (auto) {
          apply();
          return;
        }
        setPending((n) => n + 1);
      },
    });
  }, [qc, auto]);

  return {
    pending,
    lastActor,
    live,
    refresh: () => {
      for (const key of keys.current) void qc.invalidateQueries({ queryKey: key });
      setPending(0);
    },
  };
}

/** The keys a ticket list cares about. Exported so the queue and anything
 *  else counting tickets invalidate the same set. */
export const TICKET_LIST_KEYS: QueryKey[] = [qk.tickets.all];
